import type { Lang } from '../context/LanguageContext'

export interface Translations {
  nav: {
    about: string
    services: string
    employers: string
    blog: string
    contacts: string
  }
  header: {
    cta: string
  }
  hero: {
    badge: string
    h1_1: string
    h1_accent: string
    h1_2: string
    subtitle: string
    cta_primary: string
    cta_secondary: string
    stat1_val: string
    stat1_label: string
    stat2_val: string
    stat2_label: string
    stat3_val: string
    stat3_label: string
    trust: string
  }
  achievements: {
    stats: Array<{ label: string; description: string; suffix: string }>
  }
  services: {
    eyebrow: string
    title: string
    subtitle: string
    badge: string
    card1_title: string
    card1_desc: string
    card1_stat1_val: string
    card1_stat1_label: string
    card1_stat2_val: string
    card1_stat2_label: string
    card2_title: string
    card2_desc: string
    card3_title: string
    card3_desc: string
    card3_case_val: string
    card3_case_label: string
    card4_title: string
    card4_desc: string
    card4_count: string
  }
  niches: {
    eyebrow: string
    title: string
    subtitle: string
    note: string
    positions_label: string
    other_positions: string
    categories: Array<{ title: string }>
  }
  process: {
    eyebrow: string
    title: string
    subtitle: string
    steps: Array<{ title: string; description: string }>
  }
  advantages: {
    eyebrow: string
    title: string
    subtitle: string
    stat1_label: string
    stat2_val: string
    stat2_label: string
    stat3_val: string
    stat3_label: string
    items: Array<{ title: string; description: string }>
  }
  ceo: {
    eyebrow: string
    role: string
    years_label: string
    quote: string
    points: Array<{ title: string; description: string }>
  }
  testimonials: {
    eyebrow: string
    title: string
    items: Array<{ quote: string; role: string }>
  }
  contact: {
    eyebrow: string
    title: string
    subtitle: string
    phone_label: string
    email_label: string
    messenger_label: string
    trust_badge: string
    form_title: string
    name_label: string
    name_placeholder: string
    company_label: string
    company_placeholder: string
    phone_form_label: string
    phone_placeholder: string
    email_form_label: string
    email_placeholder: string
    submit: string
    submitting: string
    success_title: string
    success_subtitle: string
    send_another: string
    privacy: string
    errors: {
      name: string
      company: string
      phone_required: string
      phone_invalid: string
      email_required: string
      email_invalid: string
    }
  }
  footer: {
    description: string
    address: string
    col1_title: string
    col1_links: string[]
    col2_title: string
    col2_links: string[]
    copyright: string
  }
  blog: {
    eyebrow: string
    title: string
    all_articles: string
    read_more: string
  }
}

const UA: Translations = {
  nav: {
    about: 'Про компанію',
    services: 'Послуги',
    employers: 'Роботодавцям',
    blog: 'Блог',
    contacts: 'Контакти',
  },
  header: {
    cta: 'Залишити заявку',
  },
  hero: {
    badge: 'Понад 12 років на ринку рекрутингу',
    h1_1: 'HR‑агентство',
    h1_accent: 'нового',
    h1_2: 'покоління',
    subtitle:
      'Закриваємо вакансії будь‑якої складності в середньому за 14 днів. Персональний підхід, прозорі умови та гарантія заміни кандидата.',
    cta_primary: 'Замовити підбір персоналу',
    cta_secondary: 'Наші галузі та спеціалізації',
    stat1_val: '3 500+',
    stat1_label: 'закритих вакансій',
    stat2_val: '98%',
    stat2_label: 'успішність підбору',
    stat3_val: '500+',
    stat3_label: 'клієнтів',
    trust: 'компаній довіряють нам',
  },
  achievements: {
    stats: [
      {
        label: 'Років на ринку',
        description:
          'Працюємо з 2013 року, накопичили експертизу у десятках галузей',
        suffix: '+',
      },
      {
        label: 'Закритих вакансій',
        description:
          'Від лінійних спеціалістів до C‑level позицій у різних індустріях',
        suffix: '+',
      },
      {
        label: 'Клієнтів',
        description:
          'Компанії від стартапів до міжнародних корпорацій довіряють нам підбір',
        suffix: '+',
      },
      {
        label: 'Успішність підбору',
        description:
          'Кандидати успішно залишаються в команді після випробувального терміну',
        suffix: '%',
      },
      {
        label: 'Середній термін закриття',
        description:
          'Швидкий підбір без втрати якості — економимо ваш час та ресурси',
        suffix: ' днів',
      },
      {
        label: 'Гарантія заміни',
        description:
          'Безкоштовна заміна кандидата, якщо він не підійшов протягом випробувального терміну',
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'Що ми робимо',
    title: 'Наші послуги',
    subtitle:
      'Спеціалізуємося на пошуку ключових спеціалістів та побудові ефективних команд для вашого бізнесу',
    badge: 'Ключова спеціалізація',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Пошук та підбір топ‑менеджерів: CEO, COO, CFO, CTO, CMO та інших керівників вищої ланки. Глибоке розуміння ринку та конфіденційний підхід до кожного проєкту.',
    card1_stat1_val: '14',
    card1_stat1_label: 'днів у середньому',
    card1_stat2_val: '100%',
    card1_stat2_label: 'гарантія заміни',
    card2_title: 'Підбір IT‑спеціалістів',
    card2_desc:
      'Розробники, DevOps, QA, Product Manager, дизайнери — знаємо специфіку IT‑ринку та говоримо однією мовою з кандидатами.',
    card3_title: 'Комплектація команди',
    card3_desc:
      'Формування цілих відділів та команд під ключ — від визначення структури до виходу останнього спеціаліста.',
    card3_case_val: '6 тижнів',
    card3_case_label: 'Зібрали IT‑відділ з 15 людей',
    card4_title: "Кар'єрні консультації",
    card4_desc:
      "Оцінка кар'єрних можливостей, підготовка до співбесід, стратегія розвитку та позиціонування на ринку.",
    card4_count: '200+ консультацій',
  },
  niches: {
    eyebrow: 'Спеціалізація',
    title: 'Ніші та напрямки',
    subtitle:
      'Підбираємо спеціалістів у ключових галузях — від лінійних позицій до топ‑менеджменту',
    note: 'Працюємо з усіма офісними та управлінськими позиціями. Не спеціалізуємося на робочих спеціальностях та масмаркеті.',
    positions_label: 'позицій',
    other_positions:
      'Та інші позиції у цьому напрямку — зверніться, і ми підберемо потрібного спеціаліста',
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Фінанси та банкінг' },
      { title: 'Продажі та бізнес‑розвиток' },
      { title: 'Маркетинг та PR' },
      { title: 'HR & People' },
      { title: 'Логістика та операції' },
      { title: 'Юриспруденція' },
      { title: 'E‑commerce & Retail' },
      { title: 'Адміністрація та підтримка' },
    ],
  },
  process: {
    eyebrow: 'Як ми працюємо',
    title: 'Етапи підбору',
    subtitle:
      'Прозорий процес від першого дзвінка до виходу кандидата на роботу',
    steps: [
      {
        title: 'Бриф',
        description:
          'Вивчаємо потреби компанії, формуємо профіль ідеального кандидата',
      },
      {
        title: 'Вакансія',
        description: 'Складаємо привабливий опис та стратегію пошуку',
      },
      {
        title: 'Пошук',
        description:
          'Активний хантинг по базі 100 000+ кандидатів та відкритих джерелах',
      },
      {
        title: 'Відбір',
        description: 'Проводимо скринінг, оцінюємо компетенції та мотивацію',
      },
      {
        title: "Інтерв'ю",
        description:
          "Організовуємо зустрічі з фіналістами, збираємо зворотний зв'язок",
      },
      {
        title: 'Офер',
        description:
          'Допомагаємо узгодити умови та супроводжуємо вихід на роботу',
      },
    ],
  },
  advantages: {
    eyebrow: 'Чому ми',
    title: 'Наші переваги',
    subtitle:
      'Ми не просто закриваємо вакансії — ми будуємо команди, які рухають бізнес уперед',
    stat1_label:
      'кандидатів успішно залишаються в команді після випробувального терміну',
    stat2_val: '4.9',
    stat2_label: 'Рейтинг на Clutch',
    stat3_val: '14 днів',
    stat3_label: 'Середній time‑to‑hire',
    items: [
      {
        title: 'Широка база кандидатів',
        description:
          'Понад 100 000 перевірених спеціалістів у нашій базі даних за всіма напрямками',
      },
      {
        title: 'Персональний підхід',
        description:
          'Персональний рекрутер для кожного проєкту, глибоке занурення у специфіку бізнесу',
      },
      {
        title: 'Економія часу та витрат',
        description:
          'Середній термін закриття — 14 днів. Ви економите ресурси на пошук та відбір',
      },
      {
        title: 'Прозорі ціни',
        description:
          'Фіксована вартість без прихованих платежів. Оплата за фактом виходу кандидата',
      },
      {
        title: 'Гарантія заміни',
        description:
          'Якщо кандидат не підійшов протягом випробувального терміну — підберемо заміну безкоштовно',
      },
      {
        title: 'Аналітика та звітність',
        description:
          'Щотижневі звіти про хід підбору, аналітика ринку та рекомендації щодо умов',
      },
    ],
  },
  ceo: {
    eyebrow: 'Засновник агентства',
    role: 'CEO & Founder ESCA',
    years_label: 'років на ринку',
    quote:
      '«Ми знаємо все про болі компаній по формуванню кадрового складу будучи безпосередньо у реальному операційному бізнесі. Наше завдання — швидко та якісно вирішити ваше питання та знайти спеціаліста максимально відповідного для вашої вакансії!»',
    points: [
      {
        title: 'Реальний бізнес‑досвід.',
        description:
          'Розуміння потреб компаній зсередини — не теоретично, а з практики управління операційним бізнесом.',
      },
      {
        title: 'Executive Search експертиза.',
        description:
          'Спеціалізація на пошуку C‑level та ключових спеціалістів для бізнесу різних масштабів.',
      },
      {
        title: 'Персональна залученість.',
        description:
          'Особисто контролює ключові проєкти та гарантує якість підбору на кожному етапі.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Відгуки',
    title: 'Нам довіряють',
    items: [
      {
        quote:
          'ESCA закрили 12 вакансій за місяць — від менеджерів з продажу до фінансового директора. Якість кандидатів перевершила очікування.',
        role: 'HR Director',
      },
      {
        quote:
          'Працюємо з агентством вже 3 роки. Цінуємо швидкість, прозорість і те, що рекрутери дійсно розуміють нашу індустрію.',
        role: 'CEO',
      },
      {
        quote:
          'Завдяки ESCA ми зібрали команду розробки з нуля за 2 місяці. Усі 8 спеціалістів пройшли випробувальний термін.',
        role: 'CTO',
      },
      {
        quote:
          'Чудова робота з C‑level позиціями. Знайшли нам COO, який за півроку збільшив операційну ефективність на 30%.',
        role: 'Засновник',
      },
    ],
  },
  contact: {
    eyebrow: "Зв'яжіться з нами",
    title: 'Залишити заявку',
    subtitle:
      "Розкажіть про вашу задачу — ми зв'яжемося протягом 2 годин та запропонуємо рішення",
    phone_label: 'Телефон',
    email_label: 'E‑mail',
    messenger_label: 'Або напишіть нам у месенджер:',
    trust_badge: 'Відповідаємо протягом 2 годин у робочий час',
    form_title: 'Заповніть форму',
    name_label: "Ім'я *",
    name_placeholder: "Ваше ім'я",
    company_label: 'Компанія *',
    company_placeholder: 'Назва компанії',
    phone_form_label: 'Телефон *',
    phone_placeholder: '+38 (0XX) XXX‑XX‑XX',
    email_form_label: 'E‑mail *',
    email_placeholder: 'your@email.com',
    submit: 'Надіслати заявку',
    submitting: 'Надсилання...',
    success_title: 'Заявку надіслано!',
    success_subtitle: "Ми зв'яжемося з вами протягом 2 годин у робочий час",
    send_another: 'Надіслати ще одну заявку',
    privacy:
      'Натискаючи «Надіслати», ви погоджуєтесь з політикою конфіденційності',
    errors: {
      name: "Введіть ім'я",
      company: 'Введіть назву компанії',
      phone_required: 'Введіть телефон',
      phone_invalid: 'Некоректний номер',
      email_required: 'Введіть e‑mail',
      email_invalid: 'Некоректний e‑mail',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. Допомагаємо бізнесу знаходити найкращих спеціалістів, а професіоналам — роботу мрії.',
    address: 'м. Київ, вул. Хрещатик, 22, офіс 501',
    col1_title: 'Компанія',
    col1_links: ['Про нас', 'Послуги', 'Блог', 'Контакти'],
    col2_title: 'Послуги',
    col2_links: [
      'Підбір персоналу',
      'Аутсорсинг',
      'HR‑адміністрування',
      'Консалтинг',
    ],
    copyright: '© 2026 ESCA. Усі права захищені.',
  },
  blog: {
    eyebrow: 'Блог',
    title: 'Статті та кейси',
    all_articles: 'Усі статті',
    read_more: 'Читати далі',
  },
}

const RU: Translations = {
  nav: {
    about: 'О компании',
    services: 'Услуги',
    employers: 'Работодателям',
    blog: 'Блог',
    contacts: 'Контакты',
  },
  header: {
    cta: 'Оставить заявку',
  },
  hero: {
    badge: 'Более 12 лет на рынке рекрутинга',
    h1_1: 'HR‑агентство',
    h1_accent: 'нового',
    h1_2: 'поколения',
    subtitle:
      'Закрываем вакансии любой сложности в среднем за 14 дней. Персональный подход, прозрачные условия и гарантия замены кандидата.',
    cta_primary: 'Заказать подбор персонала',
    cta_secondary: 'Наши отрасли и специализации',
    stat1_val: '3 500+',
    stat1_label: 'закрытых вакансий',
    stat2_val: '98%',
    stat2_label: 'успешность подбора',
    stat3_val: '500+',
    stat3_label: 'клиентов',
    trust: 'компаний доверяют нам',
  },
  achievements: {
    stats: [
      {
        label: 'Лет на рынке',
        description:
          'Работаем с 2013 года, накопили экспертизу в десятках отраслей',
        suffix: '+',
      },
      {
        label: 'Закрытых вакансий',
        description:
          'От линейных специалистов до C‑level позиций в разных индустриях',
        suffix: '+',
      },
      {
        label: 'Клиентов',
        description:
          'Компании от стартапов до международных корпораций доверяют нам подбор',
        suffix: '+',
      },
      {
        label: 'Успешность подбора',
        description:
          'Кандидаты успешно остаются в команде после испытательного срока',
        suffix: '%',
      },
      {
        label: 'Средний срок закрытия',
        description:
          'Быстрый подбор без потери качества — экономим ваше время и ресурсы',
        suffix: ' дней',
      },
      {
        label: 'Гарантия замены',
        description:
          'Бесплатная замена кандидата, если он не подошёл в течение испытательного срока',
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'Что мы делаем',
    title: 'Наши услуги',
    subtitle:
      'Специализируемся на поиске ключевых специалистов и построении эффективных команд для вашего бизнеса',
    badge: 'Ключевая специализация',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Поиск и подбор топ‑менеджеров: CEO, COO, CFO, CTO, CMO и других руководителей высшего звена. Глубокое понимание рынка и конфиденциальный подход к каждому проекту.',
    card1_stat1_val: '14',
    card1_stat1_label: 'дней в среднем',
    card1_stat2_val: '100%',
    card1_stat2_label: 'гарантия замены',
    card2_title: 'Подбор IT‑специалистов',
    card2_desc:
      'Разработчики, DevOps, QA, Product Manager, дизайнеры — знаем специфику IT‑рынка и говорим на одном языке с кандидатами.',
    card3_title: 'Комплектация команды',
    card3_desc:
      'Формирование целых отделов и команд под ключ — от определения структуры до выхода последнего специалиста.',
    card3_case_val: '6 недель',
    card3_case_label: 'Собрали IT‑отдел из 15 человек',
    card4_title: 'Карьерные консультации',
    card4_desc:
      'Оценка карьерных возможностей, подготовка к собеседованиям, стратегия развития и позиционирование на рынке.',
    card4_count: '200+ консультаций',
  },
  niches: {
    eyebrow: 'Специализация',
    title: 'Ниши и направления',
    subtitle:
      'Подбираем специалистов в ключевых отраслях — от линейных позиций до топ‑менеджмента',
    note: 'Работаем со всеми офисными и управленческими позициями. Не специализируемся на рабочих специальностях и массмаркете.',
    positions_label: 'позиций',
    other_positions:
      'И другие позиции в этом направлении — обратитесь, и мы подберём нужного специалиста',
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Финансы и банкинг' },
      { title: 'Продажи и бизнес‑развитие' },
      { title: 'Маркетинг и PR' },
      { title: 'HR & People' },
      { title: 'Логистика и операции' },
      { title: 'Юриспруденция' },
      { title: 'E‑commerce & Retail' },
      { title: 'Администрация и поддержка' },
    ],
  },
  process: {
    eyebrow: 'Как мы работаем',
    title: 'Этапы подбора',
    subtitle:
      'Прозрачный процесс от первого звонка до выхода кандидата на работу',
    steps: [
      {
        title: 'Бриф',
        description:
          'Изучаем потребности компании, формируем профиль идеального кандидата',
      },
      {
        title: 'Вакансия',
        description: 'Составляем привлекательное описание и стратегию поиска',
      },
      {
        title: 'Поиск',
        description:
          'Активный хантинг по базе 100 000+ кандидатов и открытым источникам',
      },
      {
        title: 'Отбор',
        description: 'Проводим скрининг, оцениваем компетенции и мотивацию',
      },
      {
        title: 'Интервью',
        description:
          'Организуем встречи с финалистами, собираем обратную связь',
      },
      {
        title: 'Оффер',
        description:
          'Помогаем согласовать условия и сопровождаем выход на работу',
      },
    ],
  },
  advantages: {
    eyebrow: 'Почему мы',
    title: 'Наши преимущества',
    subtitle:
      'Мы не просто закрываем вакансии — мы строим команды, которые двигают бизнес вперёд',
    stat1_label:
      'кандидатов успешно остаются в команде после испытательного срока',
    stat2_val: '4.9',
    stat2_label: 'Рейтинг на Clutch',
    stat3_val: '14 дней',
    stat3_label: 'Средний time‑to‑hire',
    items: [
      {
        title: 'Широкая база кандидатов',
        description:
          'Более 100 000 проверенных специалистов в нашей базе данных по всем направлениям',
      },
      {
        title: 'Персональный подход',
        description:
          'Персональный рекрутер для каждого проекта, глубокое погружение в специфику бизнеса',
      },
      {
        title: 'Экономия времени и затрат',
        description:
          'Средний срок закрытия — 14 дней. Вы экономите ресурсы на поиск и отбор',
      },
      {
        title: 'Прозрачные цены',
        description:
          'Фиксированная стоимость без скрытых платежей. Оплата по факту выхода кандидата',
      },
      {
        title: 'Гарантия замены',
        description:
          'Если кандидат не подошёл в течение испытательного срока — подберём замену бесплатно',
      },
      {
        title: 'Аналитика и отчётность',
        description:
          'Еженедельные отчёты о ходе подбора, аналитика рынка и рекомендации по условиям',
      },
    ],
  },
  ceo: {
    eyebrow: 'Основатель агентства',
    role: 'CEO & Founder ESCA',
    years_label: 'лет на рынке',
    quote:
      '«Мы знаем всё о болях компаний по формированию кадрового состава, находясь непосредственно в реальном операционном бизнесе. Наша задача — быстро и качественно решить ваш вопрос и найти специалиста, максимально соответствующего вашей вакансии!»',
    points: [
      {
        title: 'Реальный бизнес‑опыт.',
        description:
          'Понимание потребностей компаний изнутри — не теоретически, а из практики управления операционным бизнесом.',
      },
      {
        title: 'Экспертиза Executive Search.',
        description:
          'Специализация на поиске C‑level и ключевых специалистов для бизнеса разных масштабов.',
      },
      {
        title: 'Личная вовлечённость.',
        description:
          'Лично контролирует ключевые проекты и гарантирует качество подбора на каждом этапе.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Отзывы',
    title: 'Нам доверяют',
    items: [
      {
        quote:
          'ESCA закрыли 12 вакансий за месяц — от менеджеров по продажам до финансового директора. Качество кандидатов превзошло ожидания.',
        role: 'HR Director',
      },
      {
        quote:
          'Работаем с агентством уже 3 года. Ценим скорость, прозрачность и то, что рекрутеры действительно понимают нашу индустрию.',
        role: 'CEO',
      },
      {
        quote:
          'Благодаря ESCA мы собрали команду разработки с нуля за 2 месяца. Все 8 специалистов прошли испытательный срок.',
        role: 'CTO',
      },
      {
        quote:
          'Отличная работа с C‑level позициями. Нашли нам COO, который за полгода увеличил операционную эффективность на 30%.',
        role: 'Основатель',
      },
    ],
  },
  contact: {
    eyebrow: 'Свяжитесь с нами',
    title: 'Оставить заявку',
    subtitle:
      'Расскажите о вашей задаче — мы свяжемся в течение 2 часов и предложим решение',
    phone_label: 'Телефон',
    email_label: 'E‑mail',
    messenger_label: 'Или напишите нам в мессенджер:',
    trust_badge: 'Отвечаем в течение 2 часов в рабочее время',
    form_title: 'Заполните форму',
    name_label: 'Имя *',
    name_placeholder: 'Ваше имя',
    company_label: 'Компания *',
    company_placeholder: 'Название компании',
    phone_form_label: 'Телефон *',
    phone_placeholder: '+7 (XXX) XXX‑XX‑XX',
    email_form_label: 'E‑mail *',
    email_placeholder: 'your@email.com',
    submit: 'Отправить заявку',
    submitting: 'Отправка...',
    success_title: 'Заявка отправлена!',
    success_subtitle: 'Мы свяжемся с вами в течение 2 часов в рабочее время',
    send_another: 'Отправить ещё одну заявку',
    privacy:
      'Нажимая «Отправить», вы соглашаетесь с политикой конфиденциальности',
    errors: {
      name: 'Введите имя',
      company: 'Введите название компании',
      phone_required: 'Введите телефон',
      phone_invalid: 'Некорректный номер',
      email_required: 'Введите e‑mail',
      email_invalid: 'Некорректный e‑mail',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. Помогаем бизнесу находить лучших специалистов, а профессионалам — работу мечты.',
    address: 'г. Киев, ул. Крещатик, 22, офис 501',
    col1_title: 'Компания',
    col1_links: ['О нас', 'Услуги', 'Блог', 'Контакты'],
    col2_title: 'Услуги',
    col2_links: [
      'Подбор персонала',
      'Аутсорсинг',
      'HR‑администрирование',
      'Консалтинг',
    ],
    copyright: '© 2026 ESCA. Все права защищены.',
  },
  blog: {
    eyebrow: 'Блог',
    title: 'Статьи и кейсы',
    all_articles: 'Все статьи',
    read_more: 'Читать далее',
  },
}

const EN: Translations = {
  nav: {
    about: 'About',
    services: 'Services',
    employers: 'For Employers',
    blog: 'Blog',
    contacts: 'Contact',
  },
  header: {
    cta: 'Get in Touch',
  },
  hero: {
    badge: 'Over 12 years in recruitment',
    h1_1: 'Next‑generation',
    h1_accent: 'HR',
    h1_2: 'agency',
    subtitle:
      'We fill vacancies of any complexity in an average of 14 days. Personal approach, transparent terms, and a candidate replacement guarantee.',
    cta_primary: 'Order Recruitment',
    cta_secondary: 'Our Industries & Specializations',
    stat1_val: '3,500+',
    stat1_label: 'positions filled',
    stat2_val: '98%',
    stat2_label: 'placement success rate',
    stat3_val: '500+',
    stat3_label: 'clients',
    trust: 'companies trust us',
  },
  achievements: {
    stats: [
      {
        label: 'Years on the market',
        description:
          'Operating since 2013, with expertise across dozens of industries',
        suffix: '+',
      },
      {
        label: 'Positions filled',
        description:
          'From junior specialists to C‑level executives across various industries',
        suffix: '+',
      },
      {
        label: 'Clients',
        description:
          'Companies from startups to international corporations trust us with their hiring',
        suffix: '+',
      },
      {
        label: 'Placement success rate',
        description:
          'Candidates successfully remain with the team after the probation period',
        suffix: '%',
      },
      {
        label: 'Average time to fill',
        description:
          'Fast recruitment without sacrificing quality — saving your time and resources',
        suffix: ' days',
      },
      {
        label: 'Replacement guarantee',
        description:
          "Free candidate replacement if they don't work out during the probation period",
        suffix: '%',
      },
    ],
  },
  services: {
    eyebrow: 'What we do',
    title: 'Our Services',
    subtitle:
      'We specialize in finding key talent and building effective teams for your business',
    badge: 'Core Specialization',
    card1_title: 'Executive Search (C‑level)',
    card1_desc:
      'Search and selection of top managers: CEO, COO, CFO, CTO, CMO and other senior executives. Deep market understanding and a confidential approach to every project.',
    card1_stat1_val: '14',
    card1_stat1_label: 'days on average',
    card1_stat2_val: '100%',
    card1_stat2_label: 'replacement guarantee',
    card2_title: 'IT Recruitment',
    card2_desc:
      'Developers, DevOps, QA, Product Managers, designers — we know the IT market inside out and speak the same language as candidates.',
    card3_title: 'Team Building',
    card3_desc:
      'Building entire departments and teams turnkey — from defining the structure to onboarding the last specialist.',
    card3_case_val: '6 weeks',
    card3_case_label: 'Built an IT team of 15 people',
    card4_title: 'Career Consulting',
    card4_desc:
      'Career opportunity assessment, interview preparation, development strategy and market positioning.',
    card4_count: '200+ consultations',
  },
  niches: {
    eyebrow: 'Specialization',
    title: 'Industries & Directions',
    subtitle:
      'We recruit specialists across key industries — from junior roles to top management',
    note: 'We work with all office and management positions. We do not specialize in blue-collar or mass-market roles.',
    positions_label: 'positions',
    other_positions:
      "And other positions in this direction — contact us and we'll find the right specialist",
    categories: [
      { title: 'C‑level & Executive' },
      { title: 'IT & Digital' },
      { title: 'Finance & Banking' },
      { title: 'Sales & Business Development' },
      { title: 'Marketing & PR' },
      { title: 'HR & People' },
      { title: 'Logistics & Operations' },
      { title: 'Legal' },
      { title: 'E‑commerce & Retail' },
      { title: 'Administration & Support' },
    ],
  },
  process: {
    eyebrow: 'How we work',
    title: 'Recruitment Process',
    subtitle:
      "A transparent process from the first call to the candidate's first day",
    steps: [
      {
        title: 'Brief',
        description:
          "We study the company's needs and build the ideal candidate profile",
      },
      {
        title: 'Job Posting',
        description:
          'We craft an attractive job description and search strategy',
      },
      {
        title: 'Search',
        description:
          'Active headhunting from a database of 100,000+ candidates and open sources',
      },
      {
        title: 'Screening',
        description: 'We screen candidates, assess competencies and motivation',
      },
      {
        title: 'Interview',
        description: 'We organize meetings with finalists and collect feedback',
      },
      {
        title: 'Offer',
        description:
          'We help negotiate terms and support the onboarding process',
      },
    ],
  },
  advantages: {
    eyebrow: 'Why us',
    title: 'Our Advantages',
    subtitle:
      "We don't just fill vacancies — we build teams that drive business forward",
    stat1_label:
      'of candidates successfully stay with the team after the probation period',
    stat2_val: '4.9',
    stat2_label: 'Rating on Clutch',
    stat3_val: '14 days',
    stat3_label: 'Average time‑to‑hire',
    items: [
      {
        title: 'Extensive candidate database',
        description:
          'Over 100,000 verified specialists in our database across all directions',
      },
      {
        title: 'Personal approach',
        description:
          'A dedicated recruiter for every project with deep immersion in your business specifics',
      },
      {
        title: 'Time and cost savings',
        description:
          'Average time to fill — 14 days. You save resources on search and selection',
      },
      {
        title: 'Transparent pricing',
        description:
          "Fixed cost with no hidden fees. Payment upon the candidate's start date",
      },
      {
        title: 'Replacement guarantee',
        description:
          "If the candidate doesn't work out during the probation period — we'll find a replacement for free",
      },
      {
        title: 'Analytics & reporting',
        description:
          'Weekly recruitment progress reports, market analytics and compensation recommendations',
      },
    ],
  },
  ceo: {
    eyebrow: 'Agency Founder',
    role: 'CEO & Founder ESCA',
    years_label: 'years on the market',
    quote:
      '"We know everything about the pain points companies face when building their teams, having been directly involved in real operational business. Our goal is to quickly and effectively solve your hiring challenge and find the specialist who is the perfect fit for your vacancy!"',
    points: [
      {
        title: 'Real business experience.',
        description:
          'Understanding company needs from the inside — not theoretically, but from hands-on operational management experience.',
      },
      {
        title: 'Executive Search expertise.',
        description:
          'Specialization in finding C‑level and key specialists for businesses of all sizes.',
      },
      {
        title: 'Personal involvement.',
        description:
          'Personally oversees key projects and guarantees recruitment quality at every stage.',
      },
    ],
  },
  testimonials: {
    eyebrow: 'Testimonials',
    title: 'Trusted by',
    items: [
      {
        quote:
          'ESCA filled 12 vacancies in one month — from sales managers to the CFO. The quality of candidates exceeded our expectations.',
        role: 'HR Director',
      },
      {
        quote:
          "We've been working with the agency for 3 years. We value the speed, transparency, and the fact that recruiters truly understand our industry.",
        role: 'CEO',
      },
      {
        quote:
          'Thanks to ESCA, we built a development team from scratch in 2 months. All 8 specialists passed their probation period.',
        role: 'CTO',
      },
      {
        quote:
          'Excellent work with C‑level positions. They found us a COO who increased operational efficiency by 30% in six months.',
        role: 'Founder',
      },
    ],
  },
  contact: {
    eyebrow: 'Get in touch',
    title: 'Leave a Request',
    subtitle:
      "Tell us about your challenge — we'll get back to you within 2 hours and propose a solution",
    phone_label: 'Phone',
    email_label: 'E‑mail',
    messenger_label: 'Or message us on:',
    trust_badge: 'We respond within 2 hours during business hours',
    form_title: 'Fill out the form',
    name_label: 'Name *',
    name_placeholder: 'Your name',
    company_label: 'Company *',
    company_placeholder: 'Company name',
    phone_form_label: 'Phone *',
    phone_placeholder: '+1 (XXX) XXX‑XXXX',
    email_form_label: 'E‑mail *',
    email_placeholder: 'your@email.com',
    submit: 'Send Request',
    submitting: 'Sending...',
    success_title: 'Request sent!',
    success_subtitle:
      "We'll get back to you within 2 hours during business hours",
    send_another: 'Send another request',
    privacy: 'By clicking "Send", you agree to our privacy policy',
    errors: {
      name: 'Please enter your name',
      company: 'Please enter your company name',
      phone_required: 'Please enter your phone number',
      phone_invalid: 'Invalid phone number',
      email_required: 'Please enter your email',
      email_invalid: 'Invalid email address',
    },
  },
  footer: {
    description:
      'Executive Search Consulting Agency. We help businesses find the best talent and professionals find their dream job.',
    address: 'Kyiv, Khreshchatyk St. 22, Office 501',
    col1_title: 'Company',
    col1_links: ['About us', 'Services', 'Blog', 'Contact'],
    col2_title: 'Services',
    col2_links: [
      'Recruitment',
      'Outsourcing',
      'HR Administration',
      'Consulting',
    ],
    copyright: '© 2026 ESCA. All rights reserved.',
  },
  blog: {
    eyebrow: 'Blog',
    title: 'Articles & Cases',
    all_articles: 'All articles',
    read_more: 'Read more',
  },
}

export const translations: Record<Lang, Translations> = { UA, RU, EN }

export function useTranslations(lang: Lang): Translations {
  return translations[lang]
}
